import storyGuard from "../assets/case-studies/oracle-payments/story/guard.webp";
import storyTermContext from "../assets/case-studies/oracle-payments/story/term-context.webp";
import heroPoster from "../assets/case-studies/oracle-payments/hero-poster.webp";
import heroVideo from "../assets/case-studies/oracle-payments/hero.mp4";
import statusMap from "../assets/case-studies/oracle-payments/status-map.webp";
import pressRelease from "../assets/case-studies/oracle-payments/press-release-2025-10-23.webp";
import adyenTotals from "../assets/projects/oracle-adyen-totals.webp";
import myDesignTotals from "../assets/projects/oracle-my-design-totals.webp";
import adyenFilters from "../assets/projects/oracle-adyen-filters.webp";
import myDesignFilters from "../assets/projects/oracle-my-design-filters.webp";
import prescribedWireframe from "../assets/projects/oracle-prescribed-wireframe.webp";
import finalDashAnnotated from "../assets/case-studies/oracle-payments/final-dash-annotated.webp";
import wallDashCards from "../assets/case-studies/oracle-payments/wall/02-dash-cards.webp";
import wallDashAlt from "../assets/case-studies/oracle-payments/wall/27-dash-alt.webp";
import wallTxnLanding from "../assets/case-studies/oracle-payments/wall/06-txn-landing.webp";
import wallTxnDetail from "../assets/case-studies/oracle-payments/wall/07-txn-detail.webp";
import wallPayouts from "../assets/case-studies/oracle-payments/wall/08-payouts.webp";
import wallChargebacks from "../assets/case-studies/oracle-payments/wall/10-chargebacks.webp";
import wallHolders from "../assets/case-studies/oracle-payments/wall/14-account-holders.jpg";
import wallTerminals from "../assets/case-studies/oracle-payments/wall/17-terminals.webp";
import wallLocations from "../assets/case-studies/oracle-payments/wall/18-locations.webp";
import wallUsers from "../assets/case-studies/oracle-payments/wall/20-users.webp";
import wallPlatform from "../assets/case-studies/oracle-payments/wall/22-platform-accounts.jpg";
import wallFees from "../assets/case-studies/oracle-payments/wall/31-fee-profiles.webp";


const productWall = {
  caption:
    "A sample of the screens I shipped.",
  rows: [
    {
      duration: "42s",
      items: [
        { src: wallDashAlt, alt: "Payments dashboard overview" },
        { src: wallDashCards, alt: "Dashboard metric cards" },
        { src: wallTxnLanding, alt: "Transactions landing" },
        { src: wallTxnDetail, alt: "Transaction detail" },
        { src: wallPayouts, alt: "Payouts list" },
        { src: wallChargebacks, alt: "Chargebacks list" },
      ],
    },
    {
      reverse: true,
      duration: "52s",
      items: [
        { src: wallHolders, alt: "Business entities" },
        { src: wallTerminals, alt: "Terminals" },
        { src: wallLocations, alt: "Locations" },
        { src: wallUsers, alt: "Users and access" },
        { src: wallPlatform, alt: "Platform accounts" },
        { src: wallFees, alt: "Fee profiles" },
      ],
    },
  ],
};

export const oraclePaymentsCaseStudy = {
  slug: "oracle-payments",
  editorialMedia: true,
  motion: true,
  chrome: "meadow",
  paintTheme: "oracle",
  headerLayout: "stacked",
  /* Self-playing product highlight (public/films/oracle) */
  headerComponent: "oracle-highlight-film",
  headerImage: heroPoster,
  headerVideo: heroVideo,
  headerAnimated: true,
  headerImageAlt: "Oracle Payments Platform dashboard for Bob’s Burgers",
  title: ["Sole designer for Oracle’s cloud payments", "platform, used by 2,500+ organizations"],
  titleAccent: "2,500+ organizations",
  metaLayout: "facts",
  meta: {
    company: "Oracle",
    role: "Sole UX designer",
    team: ["Engineering"],
    duration: "1.5 years",
    timeline: "2022–2023",
  },
  highlightsLayout: "columns",
  /* Highlights removed to match QR Pay (2026-10-26); copy kept below, unused */
  highlights: [],
  _highlightsArchive: [
    {
      num: "01",
      title: "I made design how we decided",
      body: "I brought design and research into the center of every product decision and built a lasting partnership with engineering.",
    },
    {
      num: "02",
      title: "I built my own research path",
      body: "I analyzed UI patterns through sandbox accounts on Adyen, Stripe, and Square and built a research practice from what I could reach.",
    },
    {
      num: "03",
      title: "I shaped clarity as we built",
      body: "I defined a new enterprise payment product end to end, shipping production-ready screens as the requirements took form.",
    },
  ],
  sections: [
    {
      type: "content",
      label: "Overview",
      title: "Oracle wanted a payments platform that wasn’t tied to one industry",
      paragraphs: [
        "Oracle already had a cloud payments service, but it was specific to industries like restaurants. Oracle set out to build a new platform that wasn’t tied to one domain, like Adyen and Stripe, redesigned from scratch on its Redwood design system.",
        "I was the only designer on the product for 18 months. I owned onboarding, the dashboard, user management, settings, and developer tools. Its users ranged from finance managers to store managers and admin staff. With no other designer to check my thinking, I leaned on engineering and on evidence I could point to.",
      ],
      figure: {
        component: "payment-lifecycle",
        caption: "Oracle manages payments for its customers",
      },
    },
    {
      type: "rich",
      label: "Problem",
      title: "A finance manager opens the platform with three questions",
      blocks: [
        {
          type: "paragraph",
          text: "How much am I getting paid, how much do I owe Oracle in fees, and when do I get paid next?",
        },
        {
          type: "paragraph",
          text: "The platform had plenty of data to offer, and the easy path was to show all of it. I set out to work out what each user needed to see, while Oracle needed one platform that met market expectations across industries.",
        },
        {
          type: "goal-map",
          heads: ["User goal", "Business goal"],
          rows: [
            {
              goal: "See how much the business is getting paid, and when",
              change: "Offer one platform that isn’t tied to a single industry",
            },
            {
              goal: "Confirm the numbers are correct",
              change: "Meet market standards, so the product feels familiar",
            },
            {
              goal: "Know when something needs action",
              change: "Build on tested Redwood components and avoid costly rework",
            },
          ],
        },
      ],
    },
    {
      type: "content",
      bg: "tint",
      label: "Evidence",
      title: "With no users yet, I worked from the closest evidence I could find",
      paragraphs: [
        "This was a new product with no users yet, and Oracle’s design and research practice on it was still growing. I was delivering UI one sprint ahead of engineering, so I couldn’t wait for ideal research. I used what I could reach, and labeled assumptions as assumptions.",
      ],
      evidence: {
        statement:
          "With no end users to interview and no usage data, I grounded each decision in the closest evidence I could reach.",
        sources: [
          "The payment provider’s documentation",
          "Goals the team agreed on",
          "Products like Adyen, Stripe, and Square",
        ],
      },
      quoteAfter: true,
    },
    {
      type: "rich",
      label: "Journey",
      title: "Mapping onboarding showed where customers got stuck",
      blocks: [
        {
          type: "paragraph",
          text: "Onboarding wasn’t one task. It moved from Oracle’s payment teams to a customer’s owner, finance team, and store managers. No single screen could show that, so I mapped the journey, and it showed where things broke down: details shared on a screen-share call, rejected documents with no explanation, and customers checking their status by hand. For a product that handles people’s money, each of those is a moment that costs trust.",
        },
        { type: "figure", figure: { component: "oracle-chain" } },
      ],
    },
    {
      type: "rich",
      label: "Onboarding",
      title: "Onboarding involved several people, so a guided flow didn’t fit",
      blocks: [
        {
          type: "paragraph",
          text: "My first concept was a guided process: business details, then verification. But a guided process is built for one user going end to end, and real onboarding touches different user types. So across iterations I moved it into a shared account holder record that anyone can pick up. A record gives up some of the hand-holding of a guided flow, but each person can do their part and leave.",
        },
        { type: "figure", figure: { component: "oracle-evolve-onboarding" } },
      ],
    },
    {
      type: "rich",
      label: "Verification",
      title: "Store owners care about getting paid, so verification leads with that",
      blocks: [
        {
          type: "paragraph",
          text: "Payouts stay blocked until a business is verified, and “KYC” means little to a store owner. I renamed it Verification and led with the outcome: complete verification to start accepting payments. People finish a chore more readily when they can see what it unlocks. After that, each item shows its own status, so nobody has to guess what is holding things up.",
        },
        { type: "figure", figure: { component: "oracle-evolve-verification" } },
      ],
    },
    {
      type: "rich",
      label: "Dashboard",
      title: "Only six of the 26 available metrics helped a finance manager decide",
      blocks: [
        {
          type: "paragraph",
          text: "The first brief packed the dashboard with every available data point and a heavy task bar. As a list of requirements that made sense, but a dashboard that shows everything doesn’t tell anyone where to look. So I asked which decision the dashboard was actually meant to support.",
        },
        {
          type: "figure",
          figure: {
            src: prescribedWireframe,
            alt: "Early lo-fi dashboard wireframe from the brief",
            caption: "Before: the early lo-fi wireframe from the first brief",
            role: "document",
            preview: "full",
          },
        },
        {
          type: "paragraph",
          text: "I ran all 26 available metrics against our assumed user goals, and kept the six that supported a decision: Expected payout, Paid out, Pending payout, Total fees, Average transaction value, and Refund rate. These were assumed goals, not tested ones, so I treated the six as our best starting point.",
        },
        {
          type: "annotated-dash",
          title: "What shipped",
          src: finalDashAnnotated,
          alt: "Final Oracle Payments dashboard focused on key metrics",
          caption:
            "The final dashboard, built around those six metrics.",
          callouts: [
            {
              n: 1,
              label: "Four payout figures, then the trend",
              x: "3.2%",
              y: "12.5%",
            },
            {
              n: 2,
              label: "Scope: location and period",
              x: "76%",
              y: "9%",
            },
            {
              n: 3,
              label: "Amount or count, day to month",
              x: "74%",
              y: "27%",
            },
            {
              n: 4,
              label: "Refund rate in the heading, not a tooltip",
              x: "3.2%",
              y: "71%",
            },
          ],
        },
      ],
    },
    {
      type: "rich",
      label: "Statuses",
      title: "Users need one status they can act on, not 30+ combinations",
      blocks: [
        {
          type: "paragraph",
          text: "A business entity is the legal business, with multiple stores or locations that accept payments under it. Its card showed a separate status for every check the payment provider ran. So I asked: why so many statuses, and what’s the point of each one for the user? A status nobody can act on is noise, so I mapped every combination in the developer docs and reduced them to one.",
        },
        {
          type: "status-compare",
          compare: {
            beforeNote: "What should a user focus on?",
            afterNote: "One clear actionable status",
            caption: "Three competing statuses on a business entity card, reduced to the one the user can act on.",
          },
        },
        {
          type: "figure",
          figure: {
            src: statusMap,
            alt: "Mapping tables reconciling every payment provider status into a single actionable Oracle Payments status",
            caption:
              "I mapped every status in the developer docs (verification, terminal status, terminal assignment) into one actionable signal.",
            role: "document",
            preview: "full",
          },
        },
      ],
    },
    {
      type: "rich",
      label: "Transactions",
      title: "Merchants check the numbers first, so totals sit above the table",
      blocks: [
        {
          type: "paragraph",
          text: "Users expect a new tool to behave like the ones they already know, which is Jakob’s Law. So I studied the platforms closest to what we were building. Adyen also powers Oracle Payments, which made its merchant view the most direct comparison. I created sandbox accounts on Adyen, Stripe, and Square, and worked through the same tasks a merchant would. I wasn’t looking to copy them. I wanted to know where to follow the market and where our users needed something different.",
        },
        {
          type: "pattern-compare",
          title: "Totals before the table",
          paragraphs: [
            "Adyen leads its merchant view with account balances. I opened with five reconciliation totals, so merchants can confirm the numbers before searching through thousands of rows.",
          ],
          before: {
            label: "Adyen transaction page",
            src: adyenTotals,
            alt: "Adyen merchant transaction page with account balances",
          },
          after: {
            label: "My design",
            caption:
              "Five reconciliation totals above the table.",
            captionBelow: true,
            src: myDesignTotals,
            alt: "Oracle Payments transactions page with five reconciliation totals",
          },
        },
        {
          type: "pattern-compare",
          title: "Key filters in the search",
          paragraphs: [
            'Adyen offers presets like “Last 90 days” alongside a raw calendar. I surfaced the key filters in the smart search so users didn’t have to dig for them.',
          ],
          before: {
            label: "Adyen date filters",
            src: adyenFilters,
            alt: "Adyen date range calendar filter overlaid on the transactions table",
          },
          after: {
            label: "My design",
            caption:
              "Key filters in the smart search.",
            captionBelow: true,
            src: myDesignFilters,
            alt: "Oracle Payments smart search filters for date range and amount",
          },
        },
      ],
    },
    {
      type: "rich",
      label: "Guardrails",
      title: "Blocked actions explain why and link to the fix",
      blocks: [
        {
          type: "paragraph",
          text: "Closing an account is permanent, and money is attached to it. I didn’t want a blocked action to be a dead end. So it explains why and links to the fix, like assigning a new default account. Locations can’t be unassigned, only reassigned, so funds never lose their account.",
        },
        {
          type: "figure",
          figure: {
            src: storyGuard,
            alt: "Close Account dialog explaining why Acme West cannot be closed, with links to View Locations and Assign Default Account",
            caption: "Each blocked action explains why and links to the fix.",
            role: "document",
          },
        },
      ],
    },
    {
      type: "rich",
      bg: "tint",
      label: "Terminals",
      title: "Terminal setup took 10–12 weeks and relied on Excel",
      blocks: [
        {
          type: "paragraph",
          text: "Terminals are the last step before a customer can take a payment in person. I ran sessions with subject matter experts and mapped the existing Cerner setup: 10–12 weeks, with MAC addresses matched to terminals in Excel.",
        },
        {
          type: "paragraph",
          text: "I wrote the experience as a script, following an IT admin named Mark through each step, because a story is easier to discuss than a flow diagram. That made the case for a target of 4–5 weeks, and for a harder question: could we remove the MAC address entirely?",
        },
        {
          type: "paragraph",
          text: "Stripe and Square pair terminals with a short code, so I mocked a pairing code and framed it as value: days saved against one-time developer effort. I also explored five layouts, from drawers to full-page flows. I chose the drawer because it keeps the terminal list in context. The first release moved workstation mapping out of Excel and into the product, a first step toward that 4–5 week target.",
        },
        {
          type: "figure",
          figure: {
            src: storyTermContext,
            alt: "Terminals list with cards behind an open Register new terminal drawer",
            caption: "Registering a terminal without leaving the list.",
            role: "document",
          },
        },
      ],
    },
    {
      type: "rich",
      label: "Scope",
      title: "I also designed the rest of the product",
      blocks: [
        {
          type: "paragraph",
          text: "That included chargebacks, users and access, and the Oracle-side admin portal, alongside QR Pay and other Oracle work.",
        },
        {
          type: "paragraph",
          text: "Tickets came with detailed specs. I delivered to spec first, then asked for lo-fi exploration alongside each request, using those wireframes to ask “what if” and surface edge cases early. Delivering reliably is what gave me room to ask.",
        },
        {
          type: "product-wall",
          ...productWall,
        },
      ],
    },
    {
      type: "content",
      label: "Outcome",
      title: "Oracle Payments launched in 2024 and now runs in 16,200 venues",
      paragraphs: [
        "I handed off the V1 designs in 2023. Oracle Payments Cloud Service launched to market in 2024 and, as of Oracle’s October 2025 release, runs across restaurants, hospitality, healthcare, and sports and entertainment.",
      ],
      stats: [
        {
          value: "2,550",
          label: "organizations running it",
        },
        {
          value: "16,200",
          label: "venues across the US and UK",
        },
        {
          value: "4",
          label: "industries: restaurants, hospitality, healthcare, and sports and entertainment",
        },
      ],
      statsSource: "Source: Oracle press release, 23 October 2025.",
      statsPress: {
        href: "https://www.oracle.com/news/announcement/enterprises-rapidly-adopt-oracle-payments-powered-by-adyen-to-offer-seamless-customer-experiences-2025-10-23/",
        image: pressRelease,
        imageAlt: "Oracle newsroom press release page announcing Oracle Payments adoption",
        meta: "Oracle press release · 23 October 2025",
        title: "Enterprises Rapidly Adopt Oracle Payments, Powered by Adyen, to Offer Seamless Customer Experiences",
      },
      figure: {
        youtube: "https://www.youtube.com/watch?v=fKnmU_EoDi8",
        alt: "Oracle Payments Cloud Service product launch video",
        caption: "The shipped Oracle Payments experience",
        role: "outcome",
      },
    },
    {
      type: "reflection",
      label: "Reflection",
      title: "What I learned building Oracle Payments",
      takeaways: [
        {
          title: "Name the evidence and its limits",
          body: "Documentation, competitor products, and team-agreed goals stood in for user research. I labeled assumptions as assumptions, so the team knew what still needed testing.",
        },
        {
          title: "Deliver first, then ask for more",
          body: "Delivering reliably made it easier to ask for the “why” behind UX and for research.",
        },
        {
          title: "Push harder for user access",
          body: "Next time, I’d ask the sales and implementation teams for even five merchant conversations. That would have tested the goals we assumed before they shipped.",
        },
      ],
    },
  ],
};
